"use client";

import { InputSearch } from "@dafiti/closet-ui";
import { useResponsive } from "@dafiti/closet-ui/hooks";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import { useFilterSearch } from "../../../hooks/useFilterSearch.hook";
import type {
  ColorFacetValue,
  CustomCategoryFacet,
  CustomColorFacet,
  CustomPriceFacet,
  DefaultListFacet,
  Facet,
  FacetValue,
} from "../../../types/filters.type";
import { CustomCategoryFilter } from "../CustomCategoryFilter";
import { CustomColorFilter } from "../CustomColorFilter";
import { CustomPriceFilter } from "../CustomPriceFilter";
import { DefaultFilterList } from "../DefaultFilterList";

const SEARCH_DEBOUNCE_MS = 250;

export type FilterContentContext = "drawer" | "quickfilter";

interface FilterContentProps {
  facet: Facet;
  context?: FilterContentContext;
  onLabelChange?: (label: string) => void;
  onBackHandlerChange?: (handler: (() => void) | null) => void;
}

// ─── Style variants by context ───────────────────────────────────────────────

const STYLES = {
  drawer: {
    root: "flex flex-col flex-1 min-h-0",
    searchWrapper: "mb-4 mt-4",
    listBox: "flex flex-col flex-1 min-h-0",
    listWrapper: "flex-1 overflow-y-auto min-h-0 px-px",
  },
  quickfilter: {
    root: "flex flex-col flex-1 min-h-0 lg:max-w-[312px]",
    searchWrapper:
      "shrink-0 mb-2 mt-0 px-0 rounded-full shadow-[0_8px_16px_0_rgba(0,0,0,0.16)]",
    listBox:
      "flex flex-col flex-1 min-h-0 lg:p-4 lg:bg-white lg:border-2 lg:border-b-0 lg:border-support-100 lg:px-4 lg:py-4 lg:pb-0 lg:rounded-t-3xl lg:shadow-[0_8px_16px_0_rgba(0,0,0,0.16)]",
    listWrapper:
      "flex-1 overflow-y-auto min-h-0 max-h-[320px] pl-1 pr-4 pt-0 pb-0 lg:pl-4 lg:pt-1 lg:pb-1",
  },
} as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractValues(facet: Facet): FacetValue[] {
  if (facet.component === "Default" && "values" in facet) {
    return (facet as DefaultListFacet).values;
  }
  if (facet.component === "CustomColor") {
    return (facet as CustomColorFacet).values;
  }
  return [];
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FilterContent({
  facet,
  context = "drawer",
  onLabelChange,
  onBackHandlerChange,
}: FilterContentProps) {
  const t = useTranslations("catalog");
  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(rawQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  useEffect(() => {
    setRawQuery("");
    setDebouncedQuery("");
  }, [facet.field]);

  const allValues = useMemo(() => extractValues(facet), [facet]);
  const filteredValues = useFilterSearch(allValues, debouncedQuery);

  const { isMobile } = useResponsive();
  const showSearch =
    facet.is_searchable && !(context === "quickfilter" && isMobile);

  const displayFacet = useMemo<Facet>(() => {
    if (!debouncedQuery) return facet;

    if (facet.component === "Default" && "values" in facet) {
      return {
        ...(facet as DefaultListFacet),
        values: filteredValues as FacetValue[],
      };
    }

    if (facet.component === "CustomColor") {
      return {
        ...(facet as CustomColorFacet),
        values: filteredValues as ColorFacetValue[],
      };
    }

    return facet;
  }, [facet, filteredValues, debouncedQuery]);

  const styles = STYLES[context];
  const isCompact = context === "quickfilter" || context === "drawer";

  return (
    <div className={styles.root} data-testid="filter-content">
      {showSearch && (
        <div className={styles.searchWrapper}>
          <InputSearch
            value={rawQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setRawQuery(e.target.value)
            }
            onClear={() => setRawQuery("")}
            submitButtonType="button"
            showBackButton={false}
            placeholder={t("facet.search")}
            aria-label={t("facet.searchFacet", { label: facet.label })}
            submitAriaLabel={t("facet.submitSearch")}
            clearAriaLabel={t("facet.clearSearch")}
            data-testid="filter-search-input"
          />
        </div>
      )}

      <div className={styles.listBox}>
        <div className={styles.listWrapper} data-testid="filter-content-list">
          {displayFacet.component === "Default" && "values" in displayFacet && (
            <DefaultFilterList
              facet={displayFacet as DefaultListFacet}
              isCompact={isCompact}
            />
          )}

          {displayFacet.component === "CustomColor" && (
            <CustomColorFilter
              facet={displayFacet as CustomColorFacet}
              isCompact={isCompact}
            />
          )}

          {displayFacet.component === "CustomPrice" && (
            <CustomPriceFilter facet={displayFacet as CustomPriceFacet} />
          )}

          {displayFacet.component === "CustomCategory" && (
            <CustomCategoryFilter
              facet={displayFacet as CustomCategoryFacet}
              onLabelChange={onLabelChange}
              onBackHandlerChange={onBackHandlerChange}
              isCompact={isCompact}
            />
          )}
        </div>
      </div>
    </div>
  );
}
